import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import { Box, Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './index.scss';

/** Live match against a media query, kept in sync across resize and rotation. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

interface DesktopOnlyProps {
  /** What the user was trying to open, e.g. "Import job seekers". */
  title: string;
  /** One sentence on why this needs a desktop. */
  reason: string;
  /** Identifies the record they were heading for, when there is one. */
  recordLabel?: string;
  /** Width below which the redirect card replaces the content. Defaults to the tablet floor. */
  minWidth?: number;
  children: React.ReactNode;
}

/**
 * Renders a short redirect card instead of a broken small-screen version.
 *
 * A few flows genuinely cannot work at 390px — the CSV column mapping across
 * 20+ fields, and the three editors, which need a canvas and a 320px inspector
 * side by side. The honest answer is to say so and name the record, not to
 * ship a layout that technically renders and practically cannot be used, and
 * not to quietly drop the parts that do not fit.
 *
 * The children are not rendered at all below the threshold, so a phone never
 * mounts an editor or fires the queries behind one.
 *
 * The card hands the flow over rather than only refusing it: it shows the URL,
 * copies it in one tap so it can be pasted on a desktop, and offers a way
 * back. "Continue on desktop" used to be styled text with no handler — it
 * looked like a button, did nothing when tapped, and left no route onward or
 * back.
 */
const DesktopOnly: React.FC<DesktopOnlyProps> = ({
  title,
  reason,
  recordLabel,
  minWidth = 768,
  children,
}) => {
  const wideEnough = useMediaQuery(`(min-width: ${minWidth}px)`);
  const navigate = useNavigate();
  /**
   * null while idle. 'copied' when the clipboard took it; 'selected' when the
   * browser refused and the URL was highlighted instead — the fallback needs
   * to say so, or the tap looks like it did nothing.
   */
  const [copyState, setCopyState] = useState<'copied' | 'selected' | null>(null);

  useEffect(() => {
    if (!copyState) return;
    const t = setTimeout(() => setCopyState(null), 3000);
    return () => clearTimeout(t);
  }, [copyState]);

  if (wideEnough) return <>{children}</>;

  const href = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopyState('copied');
    } catch {
      // The clipboard needs a secure context and can still be refused by
      // permission policy. Select the URL so it can be copied by hand rather
      // than leaving the tap doing nothing — the failure this card exists to
      // remove — and say which of the two happened.
      const el = document.querySelector('.cc-desktop-only__url');
      if (!el) return;
      const range = document.createRange();
      range.selectNodeContents(el);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      setCopyState('selected');
    }
  };

  return (
    <Box className="cc-desktop-only">
      <Box className="cc-desktop-only__card">
        <Box className="cc-desktop-only__icon">
          <DesktopWindowsOutlinedIcon />
        </Box>
        {recordLabel && (
          <Typography className="cc-desktop-only__record">{recordLabel}</Typography>
        )}
        <Typography component="h2" className="cc-desktop-only__title">
          {title}
        </Typography>
        <Typography className="cc-desktop-only__reason">{reason}</Typography>

        {/* The URL, visible and selectable. The point of this card is to get
            you to this screen on a machine that can show it. */}
        <Typography className="cc-desktop-only__url">{href}</Typography>

        <Box className="cc-desktop-only__actions">
          <Button
            variant="contained"
            startIcon={copyState === 'copied' ? <CheckIcon /> : <ContentCopyOutlinedIcon />}
            onClick={copyLink}
          >
            {copyState === 'copied'
              ? 'Link copied'
              : copyState === 'selected'
                ? 'Selected — copy it'
                : 'Copy link'}
          </Button>
          <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Box>

        <Typography className="cc-desktop-only__hint">
          Needs a screen at least {minWidth}px wide.
        </Typography>
      </Box>
    </Box>
  );
};

export default DesktopOnly;
